<template>
  <div class="lead-form-content">
    <!-- 폼 필드 -->
    <v-form ref="form" class="form-fields">
      <!-- 업체명 -->
      <div class="form-field mb-3">
        <div class="field-label">업체명*</div>
        <NaverAutoComplete
          :companyName="localCompanyName"
          :x="localX"
          :y="localY"
          :roadAddress="localRoadAddress"
          @update:all="handleNaverUpdate" />
      </div>

      <!-- 연락처 -->
      <div class="form-field mb-3">
        <div class="field-label">연락처*</div>
        <TextInput
          v-model="localContact"
          name="contact"
          type="tel"
          placeholder="숫자만 입력해주세요"
          height="48"
          :error="contactError"
          maxLength="13" />
      </div>

      <!-- 상담 가능 시간 (매장) / 이메일 (제품) -->
      <div
        v-if="(formData.type === 'STORE' && showAvailableTime) || formData.type === 'PRODUCT'"
        class="form-field mb-4">
        <div class="field-label">{{ formData.type === 'STORE' ? '상담 가능 시간*' : '이메일*' }}</div>
        <SelectInput
          v-if="formData.type === 'STORE'"
          v-model="localAvailableTime"
          :items="items"
          placeholder="시간 무관"
          height="48" />
        <TextInput
          v-else-if="formData.type === 'PRODUCT'"
          v-model="localEmail"
          name="email"
          type="email"
          placeholder="이메일을 입력해주세요"
          height="48"
          :error="emailError" />
      </div>

      <!-- 개인정보 수집/활용 동의 -->
      <div class="form-field">
        <div class="field-label">개인정보 수집/활용 동의*</div>

        <!-- 모두 동의 -->
        <div class="agreement-all">
          <v-checkbox v-model="allAgreed" label="모두 동의합니다." hide-details class="ma-0 pa-0" />
        </div>

        <!-- 세부 동의 항목 -->
        <div class="agreement-details">
          <!-- 필수: 개인정보 수집 -->
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="pa-0 agreement-header">
                <template v-slot:default>
                  <div class="d-flex align-center justify-space-between" style="width: 100%">
                    <v-checkbox
                      v-model="privacyAgreed"
                      label="개인정보 수집·이용 동의 (필수)"
                      hide-details
                      class="ma-0 pa-0 flex-grow-1"
                      @click.stop />
                  </div>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="agreement-content">
                <ul class="agreement-list">
                  <li>수집항목 : 매장명, 전화번호</li>
                  <li>보유 및 이용기간 : 5년(별도 요청시 파기)</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- 선택: 마케팅 수신 -->
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="pa-0 agreement-header">
                <template v-slot:default>
                  <div class="d-flex align-center justify-space-between" style="width: 100%">
                    <v-checkbox
                      v-model="marketingAgreed"
                      label="주요 혜택 정보 수신 동의 (선택)"
                      hide-details
                      class="ma-0 pa-0 flex-grow-1"
                      @click.stop />
                  </div>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="agreement-content">
                <ul class="agreement-list">
                  <li>슈퍼멤버스 무료 웨비나 및 이벤트 소식을 보내드려요.</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import NaverAutoComplete from '@/components/company/search/NaverAutoComplete.vue';
import TextInput from '@/components/shared/TextInput.vue';
import SelectInput from '@/components/shared/SelectInput.vue';

export default {
  name: 'LeadFormContent',

  components: {
    NaverAutoComplete,
    TextInput,
    SelectInput,
  },

  props: {
    formData: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    // 상담 가능 시간 필드 표시 여부
    showAvailableTime: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    // 전체 폼 유효성 검증
    isFormValid() {
      // 1. 업체명 검증
      const hasCompanyName = this.formData.name?.length > 0;

      // 2. 필수 필드 검증 (타입별)
      let hasRequiredField;
      if (this.formData.type === 'PRODUCT') {
        hasRequiredField = !!this.formData.email && !!this.formData.contact;
      } else {
        // STORE 타입: contact + availableTime (showAvailableTime이 true일 때만)
        hasRequiredField = !!this.formData.contact && (!this.showAvailableTime || !!this.formData.availableTime);
      }

      // 3. 필드 형식 검증 (에러 없음)
      const fieldsValid = !this.contactError && (this.formData.type === 'PRODUCT' ? !this.emailError : true);

      // 4. 개인정보 동의 검증
      const hasPrivacyAgreed = this.formData.isPrivacyAgreed;

      return fieldsValid && hasCompanyName && hasRequiredField && hasPrivacyAgreed;
    },

    // 연락처 에러 메시지
    contactError() {
      const contact = this.formData.contact;
      if (!contact) return '';

      // 숫자와 하이픈(-) 이외의 문자가 포함되어 있는지 검사
      const hasInvalidChars = /[^0-9-]/.test(contact);
      if (hasInvalidChars) {
        return '숫자만 입력해주세요.';
      }

      // 하이픈 제거 후 숫자만 추출
      const numbersOnly = contact.replace(/-/g, '');

      // 전화번호 길이 검증 (10자리 또는 11자리만 허용)
      if (numbersOnly.length < 10 || numbersOnly.length > 11) {
        return '올바른 전화번호 형식이 아닙니다.';
      }

      // 전화번호 형식 검증 (010-1234-5678 또는 01012345678)
      const phoneRegex = /^01[0-9][0-9]{3,4}[0-9]{4}$/;
      if (!phoneRegex.test(numbersOnly)) {
        return '올바른 전화번호 형식이 아닙니다.';
      }
      return '';
    },

    // 이메일 에러 메시지
    emailError() {
      const email = this.formData.email;
      if (!email) return '';

      // 이메일 형식 검증
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        return '올바른 이메일 형식이 아닙니다.';
      }
      return '';
    },

    allAgreed: {
      get() {
        return this.formData.isPrivacyAgreed && this.formData.isMarketingAgreed;
      },
      set(value) {
        this.$emit('update:formData', {
          ...this.formData,
          isPrivacyAgreed: value,
          isMarketingAgreed: value,
        });
      },
    },

    privacyAgreed: {
      get() {
        return this.formData.isPrivacyAgreed;
      },
      set(value) {
        this.$emit('update:formData', { ...this.formData, isPrivacyAgreed: value });
      },
    },

    marketingAgreed: {
      get() {
        return this.formData.isMarketingAgreed;
      },
      set(value) {
        this.$emit('update:formData', { ...this.formData, isMarketingAgreed: value });
      },
    },

    localCompanyName: {
      get() {
        return this.formData.name;
      },
      set(value) {
        const newFormData = { ...this.formData, name: value };
        this.$emit('update:formData', newFormData);
      },
    },

    localContact: {
      get() {
        return this.formData.contact || '';
      },
      set(value) {
        this.$emit('update:formData', { ...this.formData, contact: value });
      },
    },

    localX: {
      get() {
        return this.formData.x;
      },
      set(value) {
        this.$emit('update:formData', { ...this.formData, x: value });
      },
    },

    localY: {
      get() {
        return this.formData.y;
      },
      set(value) {
        this.$emit('update:formData', { ...this.formData, y: value });
      },
    },

    localRoadAddress: {
      get() {
        return this.formData.roadAddress;
      },
      set(value) {
        this.$emit('update:formData', { ...this.formData, roadAddress: value });
      },
    },

    localAvailableTime: {
      get() {
        return this.formData.availableTime;
      },
      set(value) {
        this.$emit('update:formData', { ...this.formData, availableTime: value });
      },
    },

    localEmail: {
      get() {
        return this.formData.email;
      },
      set(value) {
        this.$emit('update:formData', { ...this.formData, email: value });
      },
    },
  },

  watch: {
    isFormValid: {
      handler(val) {
        this.$emit('validation', val);
      },
      immediate: true,
    },
  },

  methods: {
    handleNaverUpdate(data) {
      // NaverAutoComplete에서 한 번에 모든 값을 받아서 업데이트
      this.$emit('update:formData', {
        ...this.formData,
        name: data.companyName,
        x: data.x,
        y: data.y,
        roadAddress: data.roadAddress,
      });
    },

    formatPhoneNumber(value) {
      const numbers = value.replace(/[^0-9]/g, '');

      if (!numbers) return '';
      switch (numbers.length) {
        case 1:
        case 2:
        case 3:
        case 4:
          return numbers;
        case 5:
        case 6:
          return numbers.replace(/(\d{2})(\d+)/, '$1-$2');
        case 7:
          return numbers.replace(/(\d{3})(\d+)/, '$1-$2');
        case 8:
          return numbers.replace(/(\d{2})(\d{3})(\d{3})/, '$1-$2-$3');
        case 9:
          return numbers.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
        case 10:
          return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
        case 11:
          return numbers.replace(/(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
        default:
          return numbers;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.lead-form-content {
  width: 100%;
}

/* 폼 필드 공통 스타일 */
.form-fields {
  width: 100%;
}

.form-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 8px;
  width: 100%;
  align-self: stretch;

  .field-label {
    color: var(--default, #3a3a3a) !important;
    font-family: 'Spoqa Han Sans Neo';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 150% */
  }

  /* 모든 직접 자식 요소 전체 너비 */
  > * {
    width: 100%;
  }

  /* 중첩된 div도 전체 너비 */
  > div {
    width: 100%;
  }
}

/* NaverAutoComplete (v-autocomplete) 스타일 */
::v-deep .v-autocomplete {
  width: 100%;

  .v-input__slot {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px 16px;
    gap: 8px;
    width: 100%;
    height: 48px !important;
    min-height: 48px !important;
    background: #ffffff !important;
    border: 1px solid #dbdbdb !important;
    border-radius: 8px !important;
    margin-bottom: 0 !important;
  }

  input {
    font-family: 'Spoqa Han Sans Neo';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    color: #3a3a3a;
    padding: 0 !important;
  }

  input::placeholder {
    font-family: 'Spoqa Han Sans Neo';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    color: #dbdbdb;
  }

  .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center !important;
  }
}

/* 개인정보 동의 */
.agreement-all {
  padding: 10px;
  background: #fbfbfb !important;
  border-radius: 4px;
}

.agreement-details {
  margin-left: 10px;

  ::v-deep .v-expansion-panels {
    width: 100%;
    padding-right: 4px;

    &:not(:first-child) {
      margin-top: 8px;
    }
  }

  ::v-deep .v-expansion-panel {
    width: 100%;
    background: transparent !important;
    box-shadow: none !important;

    &::before {
      box-shadow: none !important;
    }
  }

  ::v-deep .v-expansion-panel-header {
    min-height: 24px !important;
    height: 24px !important;
    padding: 0 16px 0 0 !important;
  }

  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 0 8px 32px !important;
  }

  .agreement-text {
    font-family: 'Spoqa Han Sans Neo';
    font-size: 12px;
    line-height: 18px;
    color: #808080;
  }

  .agreement-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      font-family: 'Spoqa Han Sans Neo';
      font-size: 12px;
      line-height: 18px;
      color: #808080;
      padding-left: 12px;
      position: relative;

      &::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #808080;
      }
    }
  }
}

/* 체크박스 스타일 */
::v-deep .v-input--checkbox {
  margin: 0 !important;
  padding: 0 !important;

  .v-input__slot {
    min-height: 24px !important;
    height: 24px !important;
    margin-bottom: 0 !important;
    border: none !important;
    padding: 0 !important;
    background: transparent !important;
  }

  .v-label {
    font-family: 'Spoqa Han Sans Neo';
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #3a3a3a;
    margin-left: 8px;
  }

  .v-input--selection-controls__input {
    margin-right: 0 !important;
  }

  /* 체크박스 아이콘 색상 */
  .v-icon {
    color: #c2c2c2 !important;
  }

  /* 체크된 상태 */
  &.v-input--is-label-active .v-icon,
  &.v-input--is-dirty .v-icon {
    color: #ea2a3a !important;
  }
}

/* 드롭다운 화살표 아이콘 색상 */
.agreement-details {
  ::v-deep .v-expansion-panel-header__icon .v-icon {
    color: #c2c2c2 !important;
  }
}
</style>
